import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const SORT_OPTIONS = [
  { value: "popular", label: "Popular" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "name", label: "Name A–Z" },
];

export default function Shop() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("popular");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSearchInput(q);
    setPage(1);
  }, [q, categorySlug]);

  useEffect(() => {
    setLoading(true);
    const params = {
      page,
      limit: 24,
      sort,
      in_stock: inStockOnly,
    };
    if (categorySlug) params.category = categorySlug;
    if (q) params.q = q;
    if (maxPrice) params.max_price = Number(maxPrice);

    api
      .get("/products", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
        setPages(r.data.pages);
      })
      .finally(() => setLoading(false));
  }, [categorySlug, q, page, sort, inStockOnly, maxPrice]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === categorySlug),
    [categories, categorySlug]
  );

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="shop-page">
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">
          {activeCategory ? "Category" : q ? "Search results" : "All products"}
        </p>
        <h1 className="mt-2 font-display text-3xl font-bold text-brand-navy sm:text-4xl lg:text-5xl">
          {activeCategory ? activeCategory.name : q ? `“${q}”` : "Shop Elevation Gaming Co."}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {loading ? "Loading…" : `${total.toLocaleString()} products`}
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-teal/10 px-2 py-0.5 text-[11px] font-bold text-brand-teal">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> Square sync · live inventory
          </span>
        </p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[260px,1fr]">
        {/* Sidebar filters */}
        <aside
          className={`${mobileFiltersOpen ? "block" : "hidden"} lg:block`}
          data-testid="shop-filters"
        >
          <div className="sticky top-28 space-y-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-brand-navy">
                Filters
              </h3>
              <button
                type="button"
                className="rounded-full p-1 lg:hidden"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Close filters"
              >
                <X size={16} />
              </button>
            </div>

            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                Category
              </h4>
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/shop"
                    className={`block rounded-lg px-3 py-2 text-sm ${
                      !categorySlug ? "bg-brand-navy text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                    data-testid="filter-category-all"
                  >
                    All products
                  </Link>
                </li>
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/shop/${c.slug}`}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition ${
                        categorySlug === c.slug
                          ? "bg-brand-navy text-white"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                      data-testid={`filter-category-${c.slug}`}
                    >
                      <span>{c.name}</span>
                      <span className="text-[11px] font-semibold text-slate-400">{c.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                Availability
              </h4>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-teal focus:ring-brand-teal"
                  data-testid="filter-in-stock"
                />
                In stock only
              </label>
            </div>

            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                Max price
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">$</span>
                <input
                  type="number"
                  min="0"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="Any"
                  className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
                  data-testid="filter-max-price"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setInStockOnly(false);
                setMaxPrice("");
                setSort("popular");
              }}
              className="w-full rounded-full border border-slate-200 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 transition hover:border-brand-teal hover:text-brand-navy"
              data-testid="filter-reset"
            >
              Reset filters
            </button>
          </div>
        </aside>

        {/* Main content */}
        <div>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <form onSubmit={submitSearch} className="relative flex-1 min-w-[240px]">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search products…"
                className="h-11 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30"
                data-testid="shop-search-input"
              />
            </form>

            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-brand-navy lg:hidden"
              data-testid="mobile-filters-toggle"
            >
              <SlidersHorizontal size={16} /> Filters
            </button>

            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-11 appearance-none rounded-full border border-slate-200 bg-white pl-4 pr-9 text-sm font-semibold text-brand-navy focus:border-brand-teal focus:outline-none"
                data-testid="shop-sort-select"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    Sort · {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-72 animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="shop-empty">
              <p className="font-display text-lg font-bold text-brand-navy">No products found</p>
              <p className="mt-2 text-sm text-slate-600">Try adjusting filters or search terms.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="shop-grid">
              {items.map((p) => (
                <ProductCard key={p.id} product={p} testIdPrefix="shop" />
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="shop-pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-40"
                data-testid="pagination-prev"
              >
                ← Prev
              </button>
              <span className="text-sm font-semibold text-slate-600">
                Page {page} of {pages}
              </span>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-40"
                data-testid="pagination-next"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
